import { CalendarContext } from "@/contexts/calendarContext";
import type { Booking } from "@/types/Booking";
import type { Location } from "@/types/Location";
import type { Member } from "@/types/Member";
import type { Room } from "@/types/Room";
import type { Amenity } from "@/types/Amenity";
import { type ReactNode } from "react";

interface CalendarProviderProps {
  children: ReactNode;
  slug?: string;
  member?: Member | null;

  // UI state (managed by LocationRoomSchedule)
  date?: Date | null;
  setDate?: (date: Date | null) => void;
  calendarType?: "day" | "week";
  setCalendarType?: (type: "day" | "week") => void;
  selectedLocation?: Location | null;
  setSelectedLocation?: (location: Location | null) => void;
  selectedRoom?: Room | null;
  setSelectedRoom?: (room: Room | null) => void;

  // Filter state
  selectedRooms?: Room[];
  setSelectedRooms?: (rooms: Room[]) => void;
  selectedAmenities?: Amenity[];
  setSelectedAmenities?: (amenities: Amenity[]) => void;
  availableAmenities?: Amenity[];
  selectedRoomAmenities?: string[];
  setSelectedRoomAmenities?: (names: string[]) => void;
  availableRoomAmenities?: string[];

  // Data from React Query (read-only)
  availableLocations?: Location[] | null;
  availableRooms?: Room[] | null;
  bookings?: Booking[] | null;

  // Loading states from React Query (read-only)
  isLoading?: boolean;
  isDateLoading?: boolean;
  isLocationLoading?: boolean;
  isRoomLoading?: boolean;

  // Refetch from React Query
  refreshBookings?: () => void;
}

export const CalendarProvider = ({
  children,
  slug = "",
  member = null,

  // UI state
  date = null,
  setDate = () => {},
  calendarType = "day",
  setCalendarType = () => {},
  selectedLocation = null,
  setSelectedLocation = () => {},
  selectedRoom = null,
  setSelectedRoom = () => {},

  // Filter state
  selectedRooms = [],
  setSelectedRooms = () => {},
  selectedAmenities = [],
  setSelectedAmenities = () => {},
  availableAmenities = [],
  selectedRoomAmenities = [],
  setSelectedRoomAmenities = () => {},
  availableRoomAmenities = [],

  // Data from React Query
  availableLocations = null,
  availableRooms = null,
  bookings = null,

  // Loading states from React Query
  isLoading = false,
  isDateLoading = false,
  isLocationLoading = false,
  isRoomLoading = false,

  // Refetch from React Query
  refreshBookings,
}: CalendarProviderProps) => {
  return (
    <CalendarContext.Provider
      value={{
        slug,
        member,

        // UI state
        date,
        setDate,
        calendarType,
        setCalendarType,
        selectedLocation,
        setSelectedLocation,
        selectedRoom,
        setSelectedRoom,

        // Filter state
        selectedRooms,
        setSelectedRooms,
        selectedAmenities,
        setSelectedAmenities,
        availableAmenities,
        selectedRoomAmenities,
        setSelectedRoomAmenities,
        availableRoomAmenities,

        // Data from React Query
        availableLocations,
        availableRooms,
        bookings,

        // Loading states from React Query
        isLoading,
        isDateLoading,
        isLocationLoading,
        isRoomLoading,

        // Refetch from React Query
        refreshBookings,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};
